import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useAuth } from "utils/useAuth/useAuth";
import { trimObject, checkNonMoneyDataForTable } from "utils/utils";
import Grid from "components/Grid/Grid";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import SearchFieldButtonBar from "components/SearchFieldButtonBar/SearchFieldButtonBar";
import Divider from "components/Divider/Divider";
import Spin from "components/Spin/Spin";
import Datatable from "components/Datatable/Datatable";
import s from "./BankSubmissionFile.module.scss";
import { queryList, preSign } from "./model";

const BankSubmissionFile = () => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const gridId = "bankSubmissionFileGrid";
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [datasource, setDatasource] = useState({
    data: [],
  });
  const [search, setSearch] = useState(false);

  const [searchFieldInput, setSearchFieldInput] = useState({
    key: "",
  });
  const [focus, setFocus] = useState({});
  const [columnsArray, setColumnsArray] = useState([]);

  useEffect(() => {
    setColumnsArray([...columns]);
    const init = async () => {
      await initialFetch();
    };
    init();
  }, []);

  const initialFetch = async () => {
    setLoading(true);
    const apiResponse = await queryList({});
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.dynamicNetworkError", { data: `${t("page.finance.bankSubmission.sectionName")}` }), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            if (responseBody.data.length === 0) {
              snackbar(t("general.message.no.result"), "warning");
            }
            const first200Result = {
              ...responseBody,
              data: responseBody.data.slice(0, 20),
            };
            setDatasource(first200Result);
          } else {
            snackbar(
              t("general.message.search.error") + ": " + responseBody.message,
              "error",
            );
          }
        }
      }
    }
    setSearch(false);
    setLoading(false);
  };

  const resetInputField = () => {
    setSearchFieldInput({
      key: "",
    });
  };

  const handleReset = async () => {
    resetInputField();
    await initialFetch();
  };

  const handleSearch = async () => {
    setSearching(true);
    const params = trimObject({ ...searchFieldInput });
    const apiResponse = await queryList(params);
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.dynamicNetworkError", { data: `${t("page.finance.bankSubmission.sectionName")}` }), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            if (responseBody.data.length === 0) {
              snackbar(t("general.message.no.result"), "warning");
            }
            const first200Result = {
              ...responseBody,
              data: responseBody.data.slice(0, 20),
            };
            setDatasource(first200Result);
          } else {
            snackbar(
              t("general.message.search.error") + ": " + responseBody.message,
              "error",
            );
          }
        }
      }
    }
    setSearch(true);
    setSearching(false);
  };

  const handleDownload = async (item) => {
    const payload = { key: item.Key };
    const apiResponse = await preSign(payload);
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            snackbar(
              t("general.message.download.function.success.message"),
              "success",
            );
            if (responseBody?.data?.url) {
              window.location = responseBody?.data?.url;
            }
          } else {
            snackbar(
              t("general.message.download.function.fail.message"),
              "error",
            );
          }
        }
      }
    }
  };

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const textInputOnchange = (e, field, callback) => {
    let { value } = e.target;
    if (callback) {
      value = callback(value);
    }
    setSearchFieldInput({ ...searchFieldInput, [field]: value });
  };
  const textInputOnFocus = (field) => {
    setFocus({ [field]: true });
  };
  const textInputOnBlur = (field) => {
    setFocus({ [field]: false });
  };

  const columns = [
    {
      index: 0,
      key: "Key",
      title: t(
        "page.finance.bankSubmission.innerTab.bankSubmissionFile.columnTitle.key",
      ),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 1,
      key: "LastModified",
      title: t(
        "page.finance.bankSubmission.innerTab.bankSubmissionFile.columnTitle.created",
      ),
      sorter: false,
      render: (data, key) => {
        return moment(data[key]).format("YYYY-MM-DD HH:mm");
      },
    },
    {
      index: 2,
      key: "action",
      title: t(
        "page.finance.bankSubmission.innerTab.bankSubmissionFile.columnTitle.action",
      ),
      sorter: false,
      render: (data) => {
        return (
          <div className={s.actionBtnContainer}>
            <button onClick={() => handleDownload(data)}>
              {t(
                "page.finance.bankSubmission.innerTab.bankSubmissionFile.columnTitle.action.1",
              )}
            </button>
          </div>
        );
      },
    },
  ];

  const searchField = () => {
    return (
      <div className={s.searchFieldContainer}>
        <Grid gridId={gridId} collapse={undefined}>
          <OutlinedText
            label={t(
              "page.finance.bankSubmission.innerTab.bankSubmissionFile.key.label",
            )}
            placeholder={t(
              "page.finance.bankSubmission.innerTab.bankSubmissionFile.key.placeholder",
            )}
            value={searchFieldInput.key}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnchange(e, "key")}
            onfocusFn={() => textInputOnFocus("key")}
            onblurFn={() => textInputOnBlur("key")}
            sxParams={{ textFocus: focus.key }}
          />
        </Grid>
        <SearchFieldButtonBar
          loading={loading || searching}
          resetBtn
          handleReset={handleReset}
          searchBtn
          handleSearch={handleSearch}
        />
        <Divider margin={32} />
      </div>
    );
  };

  return (
    <div className={s.container}>
      <div className={s.contentContainer}>
        {searchField()}
        <div
          className={[s.datatableContainer, loading ? s.loadingNow : ""].join(
            " ",
          )}
        >
          {loading ? (
            <Spin />
          ) : (
            <Datatable
              columns={columnsArray}
              datasource={datasource.data}
              search={search}
            />
          )}
          {searching && <Spin type={"search"} />}
        </div>
      </div>
    </div>
  );
};

export default React.memo(BankSubmissionFile);
